import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from '@/components/Login';
import Dashboard from '@/components/Dashboard';
import CustomerList from '@/components/CustomerList';
import CustomerDetail from '@/components/CustomerDetail';
import DeliveryNotes from '@/components/DeliveryNotes';
import SignaturePad from '@/components/SignaturePad';
import Suppliers from '@/components/Suppliers';
import Orders from '@/components/Orders';
import OrderSignaturePad from '@/components/OrderSignaturePad';
import Settings from '@/components/Settings';
import Tours from '@/components/Tours';
import { Toaster } from '@/components/ui/sonner';

function App() {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  // Suppress ResizeObserver errors (harmless warning from Radix UI)
  useEffect(() => {
    const resizeObserverErrSuppressor = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErr = e;
        resizeObserverErr.stopImmediatePropagation();
        return false;
      }
    };
    window.addEventListener('error', resizeObserverErrSuppressor);
    return () => window.removeEventListener('error', resizeObserverErrSuppressor);
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        
        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        // Clear corrupted data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setIsAuthChecking(false);
      }
    };

    initAuth();
  }, []);

  const handleLogin = (token, user) => {
    try {
      setToken(token);
      setUser(user);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      console.error('Failed to save auth data:', error);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  // Show loading while checking auth state
  if (isAuthChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Login onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/customers" element={<CustomerList token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/customers/:customerId" element={<CustomerDetail token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/suppliers" element={<Suppliers token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/delivery-notes" element={<DeliveryNotes token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/sign/:noteId" element={<SignaturePad token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/orders" element={<Orders token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/orders/:orderId/sign" element={<OrderSignaturePad token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/tours" element={<Tours token={token} user={user} onLogout={handleLogout} />} />
          <Route path="/settings" element={<Settings token={token} user={user} onLogout={handleLogout} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
